<template>
    <div
        id="camera-video-player"
        ref="camera-video-player"
        class="responsive-video tw-relative tw-h-full"
        :class="[full_viewport ? 'bg-color-full-viewport' : 'bg-color', {'embed-16-by-9' : show_screenshare || multiple_presenters}, {'tw-rounded-xl ' : show_screenshare} ]"
    >
        <div v-if="multiple_presenters && !camera_is_muted || show_screenshare"
             class="tw-absolute tw-top-0 tw-left-0 tw-flex tw-justify-end tw-w-full p-16px tw-items-center tw-h-14 tw-text-sm tw-z-10 header-bg"
             :class="{'text-speaking': speaking && !microphone_is_muted}"
             id="camera-video-player-header"
        >
            <div :class="{'text-speaking': speaking && !microphone_is_muted}">
                <i class="fas tw-mr-1 tw-translate-y-px" :class="[(microphone_is_muted && show_screenshare) && 'fa-microphone-slash', !microphone_is_muted && 'fa-volume' ]"></i>
                {{ full_name }}
            </div>
        </div>
        <video class="max-vh-vw" id='camera-video-player-videoEl' ref="video" v-show="!camera_is_muted" muted/>
        <audio id='camera-video-player-audioEl' ref="audio" style="display: none;"/>

        <!-- Muted screen shown when the camera is disabled  -->
        <div v-if="camera_is_muted"
             class="tw-flex tw-flex-col tw-justify-center tw-items-center tw-absolute tw-w-full tw-h-full tw-left-0 tw-top-0 max-vh-vw">
            <pulse-svg :first_letter="full_name[0]" :speaking="speaking && !show_screenshare" :static="true"/>
            <div class="tw-flex tw-items-center tw-text-white tw-mt-1 gray-text" :class="{'text-speaking': speaking && !microphone_is_muted}">
                <i v-if="microphone_is_muted && camera_is_muted && !show_screenshare" class="tw-mr-3 mute-icon-name fas fa-microphone-slash"/>

                <span v-if="!show_screenshare"
                  class="tw-whitespace-nowrap tw-text-lg sm:tw-text-xl md:tw-text-2xl lg:tw-text-3xl">{{ full_name }}</span>
            </div>
        </div>

        <!-- Muted mic icon shown when the mic is muted  -->
        <span
            v-if="microphone_is_muted && !camera_is_muted && !show_screenshare"
            class="tw-absolute tw-right-0 tw-bottom-0 tw-m-2 tw-p-2 tw-px-3 tw-text-white mute-icon"
            :class="[{ 'mute-icon-alignment' : !multiple_presenters && !show_screenshare}]"
        >
            <i class="fas fa-lg fa-microphone-slash"/>
        </span>

        <div :class="[{'border tw-rounded-xl' : show_screenshare}, {'speaking-indicator' : speaking && !microphone_is_muted}]"/>
    </div>
</template>

<script>
import PulseSVG from './PulseSVG';

export default {
    name: 'CameraVideoPlayer',
    components: {
        'pulse-svg': PulseSVG,
    },
    props: {
        camera_is_muted: {
            required: false,
            type: Boolean,
            default: true,
        },
        full_viewport: {
            required: true,
            type: Boolean,
        },
        microphone_is_muted: {
            required: false,
            type: Boolean,
            default: true,
        },
        multiple_presenters: {
            default: false,
            type: Boolean,
        },
        participant: {
            required: true,
            type: Object,
        },
        screen_focus: {
            required: false,
            type: Boolean,
            default: false,
        },
        speaking: {
            required: false,
            type: Boolean,
            default: false,
        },
        show_screenshare: {
            type: Boolean,
            default: false,
        },
        // So the value of a range input is a DOMString, that's why we use a string for the volume instead of a number.
        // Could cast everything but thought it was a better idea to just go with the HTML specs.
        volume: {
            required: false,
            type: String,
            default: false,
        },
    },
    computed: {
        full_name() {
            if (!this.participant.metadata) {
                return '';
            }
            return `${JSON.parse(this.participant.metadata).user.firstname} ${JSON.parse(this.participant.metadata).user.lastname}`
        },
    },
    watch: {
        // video.volume is of type double, between 0 and 1, where 1 is max value.
        volume(new_volume) {
            if (this.$refs.audio) {
                this.$refs.audio.volume = parseInt(new_volume) / 100;
            }
        },
    },
    mounted() {
        this.$refs.audio.volume = parseInt(this.volume) / 100;
    }
}
</script>

<style scoped lang="scss">
.mute-icon {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 0.571rem;
    padding: 0.743rem 0.571rem;
    width: 2.857rem;
    height: 2.857rem;

    i {
        font-size: 1.25em;
    }
}

.mute-icon-name {
    transform: translateY(2px);
}

.header-bg {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0) 100%);
    color: rgba(255, 255, 255, 0.60);
}

.p-16px {
    padding: 1.143rem;
}

.border {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    border-color: #36393F;
}

.speaking-indicator {
    -webkit-box-shadow: inset 0 0 0 2px #00E89C, inset 0 0 0 3px #00E89C;
    box-shadow: inset 0 0 0 2px #00E89C, inset 0 0 0 3px #00E89C;
}

.bg-color-full-viewport {
    background: #37474F;
}

.bg-color {
    background: #2F2F2F;
}

.max-vh-vw {
    max-width: 100vw;
    max-height: 100vh;
}

.mute-icon-alignment {
    top: 63px;
    right: 16px;
}

.text-not-speaking {
    color: #FFFFFF;
    opacity: 0.6;
}

.text-speaking {
    transition: color 100ms linear;
    color: #FFFFFF !important;
    opacity: 1 !important;
}

.gray-text {
    opacity: 0.6;
}
</style>
